import { Point } from "../Point";
import { Bounds } from "../../../utilities/BoundsObserver/Bounds";

/**
 * Gets the adjusted location of the tooltip to attempt to keep it visible within the viewport.
 * @param origin The origin point to adjust from.
 * @param tooltipBounds The tooltip bounds.
 * @param containerBounds The container bounds.
 * @param alignment The alignment.
 * @returns The adjusted location of the tooltip alongside how much the tooltip was adjusted.
 */
export function getAdjustedTooltipPosition(
  origin: Point,
  tooltipBounds: Bounds,
  containerBounds: Bounds,
  alignment: "top" | "bottom" | "left" | "right" | "cursor",
): [position: Point, offset: Point] {
  const clientWidth = containerBounds.width;
  const clientHeight = containerBounds.height;

  let { x, y } = origin;

  // TODO: add a TooltipBounds component which allows us to define an area which, if the origin goes outside of those coordinates it hides the tooltip. This is useful for situations where, for example, we want to hide tooltips in scrollable areas.

  // adjust origin by the container bounds offset.
  x -= containerBounds.left;
  y -= containerBounds.top;

  const offset = { x: 0, y: 0 };

  // Shift the tooltip to the desired final position relative to the origin and based on the alignment.
  switch (alignment) {
    case "top":
      x -= tooltipBounds.width * 0.5;
      y -= tooltipBounds.height + 8;
      offset.x = tooltipBounds.width * 0.5;
      break;
    case "left":
      x -= tooltipBounds.width + 8;
      y -= tooltipBounds.height * 0.5;
      offset.y = tooltipBounds.height * 0.5;
      break;
    case "right":
      x += 8;
      y -= tooltipBounds.height * 0.5;
      offset.y = tooltipBounds.height * 0.5;
      break;
    case "bottom":
      x -= tooltipBounds.width * 0.5;
      y += 8;
      offset.x = tooltipBounds.width * 0.5;
      break;
    default:
      y += 32;
      offset.y = -32;
      break;
  }

  const result = { x, y };

  // First push the tooltip up.
  if (result.y + tooltipBounds.height > clientHeight) {
    if (alignment === "cursor") {
      result.y = origin.y - containerBounds.top - tooltipBounds.height - 12;
    } else {
      const delta = result.y + tooltipBounds.height - clientHeight;
      result.y -= delta;
    }
  }

  // Then push the tooltip down.
  if (result.y < 0) {
    result.y = 0;
  }

  // First push the tooltip right.
  if (result.x + tooltipBounds.width > clientWidth) {
    const delta = result.x + tooltipBounds.width - clientWidth;
    result.x -= delta;
  }

  // Then push the tooltip left.
  if (result.x < 0) {
    result.x = 0;
  }

  const adjustedOffset = {
    x: origin.x - (result.x + containerBounds.left),
    y: origin.y - (result.y + containerBounds.top),
  };

  if (adjustedOffset.x < 8 || adjustedOffset.x > tooltipBounds.width - 8) adjustedOffset.x = offset.x;
  if (adjustedOffset.y < 8 || adjustedOffset.y > tooltipBounds.height - 8) adjustedOffset.y = offset.y;

  return [result, adjustedOffset];
}
